import { Box, Skeleton } from "@mui/material";
import { Suspense, useRef } from "react";
import type ReactQuillInstance from "react-quill";
import ErrorHelper from "@/components/ErrorWrapper";
import lazyWithRetry from "@/utils/lazy-with-retry";
import {
  RICH_TEXT_EDITOR_DEFAULT_MODULES,
  RICH_TEXT_EDITOR_FORMATS,
} from "./richTextEditorConstant";

// Quill (and its stylesheet) is only needed once an editor is on screen.
const ReactQuill = lazyWithRetry(async () => {
  await import("react-quill/dist/quill.snow.css");
  return import("react-quill");
});

interface RichTextEditorProps {
  name?: string;
  value?: string;
  onChange?: (event: { target: { name: string; value: string } }) => void;
  onBlur?: (event: { target: { name: string } }) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
}

/**
 * Rich Text Editor Component for Template Creation and Editing
 *
 * Features:
 * - Bold, Italic, Underline formatting
 * - Bulleted and Numbered Lists
 * - Font size and style
 * - Text alignment (left, center, right)
 * - Hyperlink insertion
 * - Undo/Redo
 * - Copy/Paste with formatting
 * - Placeholder/variable insertion (if enabled)
 * - Preview functionality
 * - Error handling
 * - Responsive design
 * - Optional autosave warning
 *
 * @author [Atish Bagate]
 */
const RichTextEditor = ({
  name,
  value = "",
  onChange,
  onBlur,
  error,
  placeholder = "Type Something...",
  disabled = false,
  minRows = 5,
}: RichTextEditorProps) => {
  const handleContentChange = (newContent: string) => {
    // Trigger onChange for form integration (Formik, React Hook Form, etc.)
    if (onChange && name) {
      onChange({
        target: {
          name,
          value: newContent,
        },
      });
    }
  };

  const handleBlur = () => {
    if (onBlur && name) {
      onBlur({
        target: {
          name,
        },
      });
    }
  };

  const quillRef = useRef<ReactQuillInstance>(null);

  // Pressing a toolbar control moves focus out of the editor, and the browser
  // drops the blue selection highlight the moment that happens — so opening the
  // font list appeared to deselect the text, and applying a font painted the
  // highlight back on. Suppressing the default focus shift leaves the caret and
  // the highlight in the editor for the whole interaction; Quill's own
  // mousedown/click handlers still run, so the pickers open and apply normally.
  const keepSelectionOnToolbarPress = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.(".ql-toolbar")) event.preventDefault();
  };

  // Once a font (or any picker value) has actually been applied, collapse the
  // range so the highlight clears instead of staying painted over the text that
  // was just formatted.
  const collapseSelectionAfterPick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.(".ql-picker-item")) return;

    const editor = quillRef.current?.getEditor();
    const range = editor?.getSelection();
    if (!editor || !range?.length) return;

    editor.setSelection(range.index + range.length, 0, "silent");
  };

  return (
    <ErrorHelper error={error as string}>
      <Box
        onMouseDown={keepSelectionOnToolbarPress}
        onClick={collapseSelectionAfterPick}
        sx={{
          "& .ql-container": {
            minHeight: `${minRows * 24}px`,
            maxHeight: `${minRows * 25}px`,
            overflow: "auto",
            borderBottom: error ? "2px solid #d32f2f" : undefined,
          },
          "& .ql-editor": {
            minHeight: `${minRows * 24}px`,
          },
        }}
      >
        <Suspense
          fallback={
            <Skeleton variant="rectangular" height={`${minRows * 24}px`} />
          }
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={handleContentChange}
            onBlur={handleBlur}
            modules={RICH_TEXT_EDITOR_DEFAULT_MODULES}
            formats={RICH_TEXT_EDITOR_FORMATS}
            placeholder={placeholder}
            readOnly={disabled}
            style={{
              backgroundColor: disabled ? "#f5f5f5" : "white",
            }}
          />
        </Suspense>
      </Box>
    </ErrorHelper>
  );
};

export default RichTextEditor;
